import { QuantitySelectorComponent } from '@theme/component-quantity-selector';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * @param {number} value
 * @param {number} [fallback=0]
 * @returns {number}
 */
function parseFloatOrDefault(value, fallback = 0) {
  const parsed = parseFloat(String(value));
  return Number.isFinite(parsed) ? parsed : fallback;
}

/**
 * @param {number} volume
 * @param {number} unit
 * @returns {number}
 */
function formatVolume(volume, unit) {
  const decimals = unit >= 1 ? 0 : Math.max(0, -Math.floor(Math.log10(unit)));
  return Number(volume.toFixed(decimals));
}

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
export class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * @returns {boolean}
   */
  get isVolumeMode() {
    return this.dataset.volumeMode === 'true';
  }

  /**
   * @returns {number}
   */
  get qtyUnit() {
    return parseFloatOrDefault(this.dataset.qtyUnit, 0.1);
  }

  /**
   * @returns {number}
   */
  get minCartQty() {
    return parseInt(this.refs.quantityInput.dataset.min, 10) || 6;
  }

  /**
   * Converts displayed m³ to Shopify cart quantity.
   * @param {number} volume
   * @returns {number}
   */
  volumeToCartQty(volume) {
    const unit = this.qtyUnit;
    return Math.max(this.minCartQty, Math.round(volume / unit));
  }

  /**
   * Converts Shopify cart quantity to displayed m³.
   * @param {number} cartQty
   * @returns {number}
   */
  cartQtyToVolume(cartQty) {
    return formatVolume(cartQty * this.qtyUnit, this.qtyUnit);
  }

  connectedCallback() {
    super.connectedCallback();
    this.#syncVolumeDisplayFromLineQuantity();
  }

  /**
   * Ensures the input shows m³ (0.1 per cart unit), not raw Shopify quantity.
   */
  #syncVolumeDisplayFromLineQuantity() {
    if (!this.isVolumeMode) return;

    const { quantityInput } = this.refs;
    const lineQty = parseInt(quantityInput.dataset.lineQuantity || '0', 10);
    if (lineQty > 0) {
      quantityInput.value = this.cartQtyToVolume(lineQty).toString();
    }
  }

  /**
   * Handles the quantity increase event.
   * @param {Event} event
   */
  increaseQuantity(event) {
    if (!this.isVolumeMode) {
      super.increaseQuantity(event);
      return;
    }
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();
    this.updateQuantity(1);
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event
   */
  decreaseQuantity(event) {
    if (!this.isVolumeMode) {
      super.decreaseQuantity(event);
      return;
    }
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();
    this.updateQuantity(-1);
  }

  /**
   * Updates displayed m³ by one step.
   * @param {number} stepMultiplier
   */
  updateQuantity(stepMultiplier) {
    if (!this.isVolumeMode) {
      super.updateQuantity(stepMultiplier);
      return;
    }

    const { quantityInput } = this.refs;
    const { min, step, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();
    let volume = Math.min(effectiveMax ?? Infinity, Math.max(min, value + step * stepMultiplier));
    volume = formatVolume(volume, step);
    quantityInput.value = volume.toString();
    this.onQuantityChange();
    this.updateButtonStates();
  }

  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max;
  }

  /**
   * Gets current values from DOM (fresh read every time)
   * @returns {{min: number, max: number|null, step: number, value: number, cartQuantity: number}}
   */
  getCurrentValues() {
    if (!this.isVolumeMode) {
      return super.getCurrentValues();
    }

    const { quantityInput } = this.refs;

    return {
      min: parseFloatOrDefault(quantityInput.min, 0.6),
      max: quantityInput.max ? parseFloatOrDefault(quantityInput.max, null) : null,
      step: parseFloatOrDefault(quantityInput.step, this.qtyUnit),
      value: parseFloatOrDefault(quantityInput.value, 0.6),
      cartQuantity: parseInt(quantityInput.getAttribute('data-cart-quantity') || '0', 10) || 0,
    };
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    minusButton.disabled = value <= min;
    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
  }

  /**
   * Handles the quantity set event (on blur).
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!this.isVolumeMode) {
      super.setQuantity(event);
      return;
    }

    if (!(event.target instanceof HTMLInputElement)) return;

    event.preventDefault();
    const { quantityInput } = this.refs;
    const { min, step } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    let volume = parseFloatOrDefault(event.target.value, min);
    volume = Math.min(effectiveMax ?? Infinity, Math.max(min, volume));
    volume = formatVolume(Math.round(volume / step) * step, step);

    quantityInput.value = volume.toString();
    this.onQuantityChange();
    this.updateButtonStates();
  }

  /**
   * Handles the quantity change event.
   */
  onQuantityChange() {
    if (!this.isVolumeMode) {
      super.onQuantityChange();
      return;
    }

    const { quantityInput } = this.refs;
    const volume = parseFloatOrDefault(quantityInput.value, this.getCurrentValues().min);
    const cartQty = this.volumeToCartQty(volume);

    const event = new QuantitySelectorUpdateEvent(
      cartQty,
      Number(quantityInput.dataset.cartLine) || undefined
    );
    event.detail.volumeMode = true;
    event.detail.volume = volume;
    this.dispatchEvent(event);
  }

  /**
   * Updates cart quantity from data attribute and refreshes button states
   * @param {number} [cartQty]
   */
  updateCartQuantity(cartQty) {
    if (!this.isVolumeMode) {
      super.updateCartQuantity();
      return;
    }

    const { quantityInput } = this.refs;
    const nextQty =
      typeof cartQty === 'number'
        ? cartQty
        : parseInt(
            quantityInput.dataset.lineQuantity ||
              quantityInput.getAttribute('data-cart-quantity') ||
              '0',
            10
          ) || this.minCartQty;

    quantityInput.setAttribute('data-cart-quantity', nextQty.toString());
    if (quantityInput.dataset.lineQuantity != null) {
      quantityInput.dataset.lineQuantity = nextQty.toString();
    }
    quantityInput.value = this.cartQtyToVolume(nextQty).toString();
    this.updateButtonStates();
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
